import * as React from 'react';

export interface UseDebouncedOptions {
  delay?: number;
  enableQueue?: boolean;
  beginDelayBeforeResolve?: boolean;
}

export const useDebounced = (
  debouncedFunction: () => Promise<void>,
  { delay = 10, enableQueue = false, beginDelayBeforeResolve = false }: UseDebouncedOptions = {}
) => {
  const [isDebouncing, setIsDebouncing] = React.useState(false);
  const isDebouncingRef = React.useRef(isDebouncing);
  const debounceNeeded = React.useRef(false);
  const startDebounceTime = React.useRef<number | null>(null);
  const timeoutId = React.useRef<NodeJS.Timeout | null>(null);

  const triggerDebounce = React.useCallback(() => {
    /**
     * upon subsequent calls, if the function is already debouncing,
     * set a flag to queue another execution once the current ends
     */
    if (isDebouncingRef.current) {
      if (enableQueue) debounceNeeded.current = true;
      return;
    }
    isDebouncingRef.current = true;
    setIsDebouncing(true);
    if (beginDelayBeforeResolve) startDebounceTime.current = Date.now();

    debouncedFunction().then(() => {
      const finishDebounce = () => {
        isDebouncingRef.current = false;
        setIsDebouncing(false);
        if (enableQueue && debounceNeeded.current) {
          debounceNeeded.current = false;
          triggerDebounce();
        }
      };
      let timeoutDelay = delay;
      if (beginDelayBeforeResolve && startDebounceTime.current) {
        const timeElapsed = Date.now() - startDebounceTime.current;
        timeoutDelay = Math.max(0, delay - timeElapsed);
      }
      if (timeoutDelay > 0) {
        timeoutId.current = setTimeout(() => {
          finishDebounce();
        }, timeoutDelay);
      } else {
        finishDebounce();
      }
    });
  }, [debouncedFunction, delay, beginDelayBeforeResolve, enableQueue]);

  // ensure that the ref is updated when the state changes
  React.useEffect(() => {
    isDebouncingRef.current = isDebouncing;
  }, [isDebouncing]);

  React.useEffect(() => {
    return () => {
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, []);

  return { isDebouncing, triggerDebounce };
};
